import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Separator } from './layout/Separator'
import { handleSubmitResult } from '../v2_actions/form'
import { countryList } from '../v2_actions/country'
import { photographList } from '../v2_actions/photograph'
import { CurrencyValue } from './CurrencyValue'
import { showSuccess } from '../v2_actions/error'
import { useForm, Controller } from 'react-hook-form'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { TextField } from './form/TextField'
import { makeStyles, Dialog, IconButton } from "@material-ui/core"
import { galleryList } from '../v2_actions/gallery'
import CloseIcon from '@material-ui/icons/Close'
import Timestamp from './Timestamp'

const formSchema = Yup.object().shape({
    price: Yup.number().required(),
})

const useStyles = makeStyles({
    dialog: {
        "& .MuiDialog-paper": {
            borderRadius: 14,
            maxWidth: 420,
            width: "100%",
            overflow: "hidden",
        },
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "18px 24px 14px",
        borderBottom: "1px solid #e0e0db",
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: 700,
        color: "#0b3556",
        margin: 0,
    },
    closeBtn: {
        color: "#7a7a76",
        padding: 4,
        "&:hover": {
            color: "#0b3556",
        },
    },
    body: {
        padding: "20px 24px 24px",
    },
    infoRow: {
        display: "flex",
        justifyContent: "space-between",
        padding: "8px 0",
        borderBottom: "1px solid #f0f0ec",
    },
    infoLabel: {
        fontSize: 13,
        color: "#7a7a76",
        fontWeight: 500,
    },
    infoValue: {
        fontSize: 13,
        color: "#1b1b1b",
        fontWeight: 600,
    },
    priceSection: {
        marginTop: 18,
    },
    priceLabel: {
        fontSize: 13,
        color: "#7a7a76",
        fontWeight: 500,
        marginBottom: 6,
    },
    priceInputWrapper: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: "#f8f8f4",
        border: "1px solid #e0e0db",
        borderRadius: 10,
        padding: "10px 14px",
        transition: "border-color .15s, box-shadow .15s",
        "&:focus-within": {
            borderColor: "#3a86d8",
            boxShadow: "0 0 0 3px rgba(58,134,216,0.12)",
        },
    },
    currencySymbol: {
        fontSize: 14,
        fontWeight: 600,
        color: "#7a7a76",
    },
    priceInput: {
        "& input": {
            border: 0,
            outline: 0,
            background: "transparent",
            fontSize: 16,
            fontWeight: 600,
            color: "#0b3556",
            padding: 0,
            width: "100%",
        },
        "& .MuiInput-underline:before, & .MuiInput-underline:after": {
            display: "none",
        },
        flex: 1,
    },
    submitButton: {
        width: "100%",
        marginTop: 20,
        padding: "12px 0",
        background: "#0b3556",
        color: "#fff",
        border: 0,
        borderRadius: 10,
        fontSize: 14,
        fontWeight: 700,
        cursor: "pointer",
        transition: ".12s",
        fontFamily: "inherit",
        "&:hover": {
            background: "#0e3f66",
        },
    },
})

export const PhotographEditor = ({photograph_id, country_id, onClose}) => {

    const classes = useStyles()
    const dispatch = useDispatch()
    const defaultValues = {price: 0}
    const photograph = useSelector(() => photographList.getObject(photograph_id))
    const country = countryList.getCountryForId(country_id)

    const formProps = useForm({resolver: yupResolver(formSchema), defaultValues: defaultValues})
    const { handleSubmit, control, clearErrors, reset, formState: { errors } } = formProps

    useEffect(() => {
        dispatch(photographList.ensureObjectLoaded(photograph_id))
    }, [photograph_id])

    useEffect(() => {
        if ( photograph && photograph.id ) {
            const values = Object.assign({}, defaultValues, photograph)
            reset(values)
        }
    }, [photograph])

    const onSave = (values) => {
        clearErrors()
        const on_ok = function(json) {
            showSuccess("Saved", "Photograph saved")
            onClose()
            dispatch(galleryList.invalidateGalleryAndPhotographs(photograph.gallery))
        }

        values.id = photograph_id
        return dispatch(photographList.saveObject(values)).then((res) => handleSubmitResult({res, formProps, on_ok: on_ok}))
    }

    return (
        <Dialog
            open={true}
            onClose={onClose}
            className={classes.dialog}
        >
          <div className={classes.header}>
            <div className={classes.headerTitle}>Photograph Details (Edit)</div>
            <IconButton className={classes.closeBtn} onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div className={classes.body}>
            <div className={classes.infoRow}>
              <div className={classes.infoLabel}>ID</div>
              <div className={classes.infoValue}>{photograph && photograph.id}</div>
            </div>
            <div className={classes.infoRow}>
              <div className={classes.infoLabel}>Photo Name</div>
              <div className={classes.infoValue}>{photograph && photograph.photo_name}</div>
            </div>
            <div className={classes.infoRow}>
              <div className={classes.infoLabel}>Taken at</div>
              <div className={classes.infoValue}>
                {photograph && <Timestamp value={photograph.photograph_taken_at} />}
              </div>
            </div>
            <div className={classes.infoRow}>
              <div className={classes.infoLabel}>Available to Purchase</div>
              <div className={classes.infoValue}>{photograph && (photograph.available_to_purchase ? "Yes" : "No")}</div>
            </div>
            <div className={classes.infoRow}>
              <div className={classes.infoLabel}>Times Sold</div>
              <div className={classes.infoValue}>{photograph && (photograph.purchased_trolley_photographs || []).length}</div>
            </div>

            <form onSubmit={handleSubmit(onSave)}>
              <div className={classes.priceSection}>
                <div className={classes.priceLabel}>Price</div>
                <div className={classes.priceInputWrapper}>
                  <div className={classes.currencySymbol}>
                    <CurrencyValue value={null} country={country} />
                  </div>
                  <Controller name="price"
                              control={control}
                              render={(fieldProps) => (
                                  <TextField fieldProps={fieldProps}
                                             type="text"
                                             variant="standard"
                                             placeholder="0"
                                             className={classes.priceInput}
                                  />
                              )}/>
                </div>
              </div>

              <button className={classes.submitButton} type="submit">
                Save
              </button>
            </form>
          </div>
        </Dialog>
    )

}
